import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ClipboardText, ArrowsClockwise, Pause, Play, Plus, CurrencyCircleDollar, CheckCircle, XCircle } from "@phosphor-icons/react";

const ACTION_META = {
  pause: { label: "Pause", icon: Pause, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  resume: { label: "Resume", icon: Play, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  update_budget: { label: "Budget", icon: CurrencyCircleDollar, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  create: { label: "Create", icon: Plus, color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30" },
};

const FILTERS = [
  { key: "all", label: "All actions" },
  { key: "pause", label: "Pause" },
  { key: "resume", label: "Resume" },
  { key: "update_budget", label: "Budget" },
  { key: "create", label: "Create" },
];

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString() + " " + new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function describeAction(entry) {
  const d = entry.details || {};
  const tt = entry.target_type || "campaign";
  const noun = tt === "adset" ? "ad set" : tt === "ad" ? "ad" : "campaign";
  switch (entry.action) {
    case "pause": return `Paused ${noun} ${entry.target_id || "—"}`;
    case "resume": return `Resumed ${noun} ${entry.target_id || "—"}`;
    case "update_budget": return `Updated budget on ${noun} ${entry.target_id || "—"} → RM${(d.new_daily_budget || 0) / 100}`;
    case "create": {
      if (tt === "campaign") return `Created campaign "${d.name || "?"}" (${d.objective || "?"}, RM${(d.daily_budget || 0) / 100}/day)`;
      if (tt === "adset") return `Created ad set "${d.name || "?"}" in campaign ${d.campaign_id || "?"} (RM${(d.daily_budget || 0) / 100}/day, ${d.optimization_goal || "?"})`;
      if (tt === "ad") return `Created ad "${d.name || "?"}" in ad set ${d.adset_id || "?"}`;
      return `Created ${noun}`;
    }
    default: return entry.action;
  }
}

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { action: filter };
      const r = await api.get("/audit-log", { params });
      setEntries(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-zinc-900 px-6 flex items-center backdrop-blur-xl bg-zinc-950/70 sticky top-0 z-10">
        <div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Audit Trail</div>
          <div className="text-sm font-medium flex items-center gap-2">
            <ClipboardText size={14} className="text-orange-500" /> Activity Log ({entries.length})
          </div>
        </div>
        <button
          data-testid="audit-refresh-button"
          onClick={load}
          className="ml-auto p-1.5 hover:bg-zinc-900 text-zinc-400 hover:text-zinc-100 transition-colors"
          title="Refresh"
        >
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="p-6">
        {/* Filter chips */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {FILTERS.map(f => (
            <button
              key={f.key}
              data-testid={`audit-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 border transition-colors ${
                filter === f.key ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading && entries.length === 0 ? (
          <div className="text-zinc-500 text-sm">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="bg-zinc-900/40 border border-zinc-800/60 p-10 text-center text-sm text-zinc-500">
            No activity yet. Pause/resume/edit a campaign to see history.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="audit-list">
            {entries.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, icon: ClipboardText, color: "text-zinc-400", bg: "bg-zinc-800/30", border: "border-zinc-800" };
              const Icon = meta.icon;
              const success = e.status === "success";
              return (
                <div
                  key={e.id}
                  data-testid={`audit-entry-${e.id}`}
                  className={`bg-zinc-900/30 border ${meta.border} pl-3 pr-4 py-2.5 flex items-center gap-3 hover:bg-zinc-900/60 transition-colors`}
                >
                  <div className={`w-7 h-7 ${meta.bg} flex items-center justify-center shrink-0`}>
                    <Icon size={13} className={meta.color} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-zinc-100 truncate">{describeAction(e)}</span>
                      {e.mode === "live" && (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-orange-500/10 text-orange-400">Live</span>
                      )}
                      {e.mode === "mock" && (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-zinc-800 text-zinc-500">Mock</span>
                      )}
                      {success ? (
                        <CheckCircle size={12} className="text-emerald-400 shrink-0" weight="fill" />
                      ) : (
                        <XCircle size={12} className="text-red-400 shrink-0" weight="fill" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-zinc-500 font-mono mt-0.5">
                      <span className="text-zinc-300">{e.display_name || e.username}</span>
                      <span>·</span>
                      <span>{relativeTime(e.created_at)}</span>
                      {e.error && <span className="text-red-400">· {e.error}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
